import { addCalendarDays } from "@common/helpers/date";
import type {
    DensityChartHighlight,
    DensityChartWindow,
} from "@design-system/DensityChart/DensityChart.lib";
import { isSegmentHighlighted } from "@design-system/DensityChart/DensityChart.lib";
import { daysBetween } from "@design-system/DensityChart/densityBuckets.lib";

interface BarClick {
    /** The clicked bar's days. */
    bar: DensityChartWindow;
    window: DensityChartWindow;
    /** The chart's days, from the history start to today. */
    bounds: DensityChartWindow;
}

/** What a click on a segment does to Updated by. */
export type SegmentClick =
    /** A selected segment: Updated by clears and the window stays. */
    | { kind: "cleared" }
    /** Updated by becomes the segment's series or person, with the window slid over its bar. */
    | { kind: "picked"; window: DensityChartWindow };

const isWithin = (days: DensityChartWindow, window: DensityChartWindow) =>
    window.start <= days.start && days.end <= window.end;

const intersect = (
    first: DensityChartWindow,
    second: DensityChartWindow,
): DensityChartWindow => ({
    start: first.start > second.start ? first.start : second.start,
    end: first.end < second.end ? first.end : second.end,
});

/**
 * The window slid, keeping its length, just far enough to cover the bar's
 * days inside `bounds`, or those days when they are longer than the window.
 * A window already covering them is returned as is, so a click on a bar's
 * track off its segments leaves it alone; a window inside `bounds` stays
 * inside them.
 */
export function slideOver({
    window,
    bar,
    bounds,
}: BarClick): DensityChartWindow {
    const shown = intersect(bar, bounds);
    if (isWithin(shown, window)) {
        return window;
    }
    const length = daysBetween(window.start, window.end);
    if (daysBetween(shown.start, shown.end) >= length) {
        return shown;
    }
    return shown.start < window.start
        ? { start: shown.start, end: addCalendarDays(shown.start, length) }
        : { start: addCalendarDays(shown.end, -length), end: shown.end };
}

/** A selected segment clears Updated by; any other picks its series or person. */
export function toSegmentClick({
    highlight,
    seriesId,
    ...click
}: BarClick & {
    highlight: DensityChartHighlight | undefined;
    seriesId: string;
}): SegmentClick {
    return isSegmentHighlighted({ highlight, seriesId, bar: click.bar })
        ? { kind: "cleared" }
        : { kind: "picked", window: slideOver(click) };
}
